/**
 * Performance Trends charts: per-module area charts plus combined line chart.
 *
 * @module SimulationPerformanceTrendsSection
 */

import { BookOpen, Headphones, Mic, Pen } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import type { ExamLevel } from "@/types/simulation-results";
import { ModuleProgressChart } from "@/components/results/ModuleProgressChart";
import { PerformanceTrendsCombinedChart } from "@/components/results/PerformanceTrendsCombinedChart";
import {
  SIMULATION_MODULE_COLORS,
  calculateAverageScores,
  type ChartDataPoint,
  type ModuleReviewTarget,
  type SimulationModuleKey,
} from "@/lib/simulationResultsChartData";
import { ROUTES } from "@/lib/routeConstants";

export interface SimulationPerformanceTrendsSectionProps {
  readonly chartData: ChartDataPoint[];
  readonly attemptedModules: ReadonlySet<SimulationModuleKey>;
  readonly averageScores: ReturnType<typeof calculateAverageScores>;
  /** Prefix for SVG gradient ids (avoid clashes if multiple instances mount) */
  readonly gradientIdPrefix: string;
  readonly moduleChartHeightClassName?: string;
  /** When false, hides the 2×2 per-module area charts (combined line chart remains). */
  readonly showPerModuleCharts?: boolean;
  /** Height class for the combined line chart plot area (default h-[300px]). */
  readonly lineChartHeightClassName?: string;
  /** AC4: latest graded review target per module. */
  readonly latestReviewByModule: Record<
    SimulationModuleKey,
    ModuleReviewTarget | null
  >;
  readonly onModuleReviewClick: (
    sessionId: string,
    module: SimulationModuleKey,
  ) => void;
  /** When set and multiple levels exist, shows a level filter in the chart header. */
  readonly levelFilter?: {
    readonly availableLevels: readonly ExamLevel[];
    readonly selectedLevel: ExamLevel;
    readonly onLevelChange: (level: ExamLevel) => void;
  };
}

export function SimulationPerformanceTrendsSection({
  chartData,
  attemptedModules,
  averageScores,
  gradientIdPrefix,
  moduleChartHeightClassName = "h-[180px] sm:h-[200px]",
  showPerModuleCharts = true,
  lineChartHeightClassName = "h-[240px] sm:h-[300px]",
  latestReviewByModule,
  onModuleReviewClick,
  levelFilter,
}: SimulationPerformanceTrendsSectionProps) {
  const { t } = useTranslation();
  const p = gradientIdPrefix;
  const nPoints = chartData.length;

  const openLatestReview = (moduleKey: SimulationModuleKey) => {
    const target = latestReviewByModule[moduleKey];
    if (target) {
      onModuleReviewClick(target.sessionId, target.module);
    }
  };

  if (nPoints === 0) {
    return (
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>
            {t("dashboard.insights.chartEmptyTitle", {
              defaultValue: "No trend data yet",
            })}
          </CardTitle>
          <CardDescription>
            {t("simulation.results.performanceTrends.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
          <p className="max-w-md text-sm text-muted-foreground">
            {t("dashboard.insights.chartEmptyDescription", {
              defaultValue:
                "Your performance-over-time chart appears after you finish at least one simulation.",
            })}
          </p>
          <Button asChild>
            <Link to={ROUTES.EXAM}>
              {t("dashboard.insights.chartEmptyCta", {
                defaultValue: "Start exam simulation",
              })}
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {showPerModuleCharts && (
        <div className="mb-5 grid grid-cols-1 gap-4 sm:mb-8 sm:gap-6 lg:grid-cols-2">
          <ModuleProgressChart
            title={
              t("simulation.results.performanceTrends.title") +
              " - " +
              t("moduleSelection.modules.reading.label", {
                defaultValue: "Reading",
              })
            }
            description={t("simulation.results.performanceTrends.subtitle")}
            icon={BookOpen}
            iconBgColor="bg-primary/10"
            iconColor="text-primary"
            data={chartData}
            dataKey="reading"
            gradientId={`${p}-readingGradient`}
            color={SIMULATION_MODULE_COLORS.reading}
            averageScore={averageScores?.reading}
            hasAttempted={attemptedModules.has("reading")}
            onOpenReview={
              latestReviewByModule.reading
                ? () => openLatestReview("reading")
                : undefined
            }
            chartHeightClassName={moduleChartHeightClassName}
          />

          <ModuleProgressChart
            title={
              t("simulation.results.performanceTrends.title") +
              " - " +
              t("moduleSelection.modules.writing.label", {
                defaultValue: "Writing",
              })
            }
            description={t("simulation.results.performanceTrends.subtitle")}
            icon={Pen}
            iconBgColor="bg-[hsl(145,60%,45%)]/10"
            iconColor="text-[hsl(145,60%,45%)]"
            data={chartData}
            dataKey="writing"
            gradientId={`${p}-writingGradient`}
            color={SIMULATION_MODULE_COLORS.writing}
            averageScore={averageScores?.writing}
            hasAttempted={attemptedModules.has("writing")}
            onOpenReview={
              latestReviewByModule.writing
                ? () => openLatestReview("writing")
                : undefined
            }
            chartHeightClassName={moduleChartHeightClassName}
          />

          <ModuleProgressChart
            title={
              t("simulation.results.performanceTrends.title") +
              " - " +
              t("moduleSelection.modules.listening.label", {
                defaultValue: "Listening",
              })
            }
            description={t("simulation.results.performanceTrends.subtitle")}
            icon={Headphones}
            iconBgColor="bg-[hsl(210,70%,55%)]/10"
            iconColor="text-[hsl(210,70%,55%)]"
            data={chartData}
            dataKey="listening"
            gradientId={`${p}-listeningGradient`}
            color={SIMULATION_MODULE_COLORS.listening}
            averageScore={averageScores?.listening}
            hasAttempted={attemptedModules.has("listening")}
            onOpenReview={
              latestReviewByModule.listening
                ? () => openLatestReview("listening")
                : undefined
            }
            chartHeightClassName={moduleChartHeightClassName}
          />

          <ModuleProgressChart
            title={
              t("simulation.results.performanceTrends.title") +
              " - " +
              t("moduleSelection.modules.speaking.label", {
                defaultValue: "Speaking",
              })
            }
            description={t("simulation.results.performanceTrends.subtitle")}
            icon={Mic}
            iconBgColor="bg-[hsl(340,70%,55%)]/10"
            iconColor="text-[hsl(340,70%,55%)]"
            data={chartData}
            dataKey="speaking"
            gradientId={`${p}-speakingGradient`}
            color={SIMULATION_MODULE_COLORS.speaking}
            averageScore={averageScores?.speaking}
            hasAttempted={attemptedModules.has("speaking")}
            onOpenReview={
              latestReviewByModule.speaking
                ? () => openLatestReview("speaking")
                : undefined
            }
            chartHeightClassName={moduleChartHeightClassName}
          />
        </div>
      )}

      <Card className="mb-5 sm:mb-8">
        <CardHeader className="p-4 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>
                {t("simulation.results.performanceTrends.title")}
              </CardTitle>
              <CardDescription>
                {t("simulation.results.performanceTrends.subtitle")}
              </CardDescription>
              {nPoints === 1 && (
                <p className="font-ui pt-1 text-sm text-muted-foreground">
                  {t("dashboard.insights.chartSingleSessionHint", {
                    defaultValue:
                      "Add more simulations to see a trend line over time.",
                  })}
                </p>
              )}
            </div>
            {levelFilter && levelFilter.availableLevels.length > 1 ? (
              <div className="flex shrink-0 items-center gap-2">
                <span className="font-ui text-sm text-muted-foreground whitespace-nowrap">
                  {t("partnerFinder.search.levelLabel")}:
                </span>
                <Select
                  value={levelFilter.selectedLevel}
                  onValueChange={levelFilter.onLevelChange}
                >
                  <SelectTrigger className="w-[140px] sm:w-[160px]">
                    <SelectValue
                      placeholder={t("partnerFinder.search.levelLabel")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {levelFilter.availableLevels.map((level) => (
                      <SelectItem key={level} value={level}>
                        {level} - {t(`levelSelection.levels.${level}.name`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
          <PerformanceTrendsCombinedChart
            chartData={chartData}
            attemptedModules={attemptedModules}
            onModuleReviewClick={openLatestReview}
            className={lineChartHeightClassName}
          />
        </CardContent>
      </Card>
    </>
  );
}
